import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, Users, FolderOpen, GitBranch, ArrowUp, RefreshCw, Info, HardDrive, CreditCard, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { StorageLimitService } from '../../lib/storage-limit-service';
import { UnifiedCreditService, type UnifiedCreditData } from '../../lib/unified-credit-service';
import { CreditService } from '../../lib/credits';
import type { UsageSummary } from '../../lib/limit-enforcement-service';
import { subscribeToCreditChanges } from '../../lib/credit-events';

interface UsageMeterWidgetProps {
  onUpgradeClick?: () => void;
  showRefreshButton?: boolean;
  showDebugInfo?: boolean;
  storageOnly?: boolean;
}

const UsageMeterWidget: React.FC<UsageMeterWidgetProps> = ({ 
  onUpgradeClick, 
  showRefreshButton = true,
  showDebugInfo = false,
  storageOnly = false
}) => {
  const { user } = useAuth();
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [creditData, setCreditData] = useState<UnifiedCreditData | null>(null);
  const [separateCredits, setSeparateCredits] = useState<{
    planCredits: number;
    purchasedCredits: number;
    totalCredits: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadUsageData = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [summary, credits, separateCounts] = await Promise.all([
        UnifiedCreditService.getUsageSummary(user.id),
        UnifiedCreditService.getCreditData(user.id),
        CreditService.getSeparateCreditCounts()
      ]);
      
      setUsageSummary(summary);
      setCreditData(credits);
      setSeparateCredits(separateCounts);
    } catch (err) {
      console.error('❌ Error loading usage data:', err);
      setError('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadUsageData();
    }
  }, [user, refreshKey, loadUsageData]);

  // Listen for real-time credit changes
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToCreditChanges(user.id, (event) => {
      console.log('💳 Credit change event received in UsageMeterWidget:', event);
      // Refresh data when credits change
      loadUsageData();
    });

    return unsubscribe;
  }, [user, loadUsageData]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const calculatePercentage = (used: number, limit: number): number => {
    if (limit === -1 || limit === 999) return 0; // Unlimited
    return Math.min((used / Math.max(limit, 1)) * 100, 100);
  };

  const formatLimit = (limit: number): string => {
    return limit === -1 || limit === 999 ? '∞' : limit.toString();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Usage Overview</h3>
          {showRefreshButton && (
            <button 
              onClick={handleRefresh}
              className="ml-auto p-1 hover:bg-gray-100 rounded"
              title="Refresh Usage Data"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
        <div className="space-y-4 animate-pulse">
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="h-16 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !usageSummary) {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="h-5 w-5 text-red-600" />
          <h3 className="text-lg font-semibold text-gray-900">Usage Overview</h3>
          <button 
            onClick={loadUsageData}
            className="ml-auto p-1 hover:bg-gray-100 rounded"
            title="Retry"
          >
            <RefreshCw className="h-4 w-4 text-gray-600" />
          </button>
        </div>
        <div className="text-center py-8">
          <p className="text-red-600 mb-3">{error || 'Unable to load usage data'}</p>
          <button 
            onClick={loadUsageData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const creditsPercentage = calculatePercentage(usageSummary.currentAnalysesUsed, usageSummary.monthlyCredits);
  const teamSeatsPercentage = calculatePercentage(usageSummary.currentTeamSeatsUsed, usageSummary.teamSeatsLimit);
  const projectsPercentage = calculatePercentage(usageSummary.currentProjectsCount, usageSummary.projectsLimit);

  return (
    <div className={`bg-white rounded-xl p-6 border border-gray-200 ${usageSummary.isOverLimit ? 'ring-2 ring-red-500' : ''}`}>
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Usage Overview</h3>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
            {usageSummary.planName}
          </span>
          {showRefreshButton && (
            <button 
              onClick={handleRefresh}
              className="p-1 hover:bg-gray-100 rounded"
              title="Refresh Usage Data"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* Debug Info */}
      {showDebugInfo && usageSummary && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Debug Information</span>
          </div>
          <div className="text-xs text-gray-600 space-y-1">
            <div>User ID: {user?.id}</div>
            <div>Plan: {usageSummary.planName} ({usageSummary.monthlyCredits} credits/month)</div>
            <div>Team Seats: {usageSummary.teamSeatsLimit === -1 ? 'Unlimited' : usageSummary.teamSeatsLimit}</div>
            <div>Projects: {usageSummary.projectsLimit === -1 ? 'Unlimited' : usageSummary.projectsLimit}</div>
            <div>Versions: {usageSummary.versionsLimit === -1 ? 'Unlimited' : usageSummary.versionsLimit}</div>
            <div>Storage: {StorageLimitService.formatBytes(usageSummary.maxStorageBytes)}</div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {!storageOnly && (
          <>
            {/* Analysis Credits */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">Analysis Credits</span>
                </div>
                <span className="text-sm text-gray-600">
                  {usageSummary.currentAnalysesUsed} / {usageSummary.monthlyCredits}
                </span>
              </div>
              
              {/* Plan Credits Bar */}
              <div className="space-y-1">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(creditsPercentage)}`}
                    style={{ width: `${creditsPercentage}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Purchased Credits Display */}
              {separateCredits && separateCredits.purchasedCredits > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Purchased Credits (backup)</span>
                    <span>{separateCredits.purchasedCredits} available</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="h-1.5 rounded-full bg-blue-500 transition-all duration-300"
                      style={{ width: '100%' }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Ad Creative Credits */}
            {creditData && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-gray-700">Creative Credits</span>
                  </div>
                  <span className="text-sm text-gray-600">
                    {creditData.adCreative.usedCredits} / {creditData.adCreative.totalCredits}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(
                      calculatePercentage(
                        creditData.adCreative.usedCredits,
                        creditData.adCreative.totalCredits
                      )
                    )}`}
                    style={{ width: `${calculatePercentage(
                      creditData.adCreative.usedCredits,
                      creditData.adCreative.totalCredits
                    )}%` }}
                  ></div>
                </div>
                {creditData.adCreative.purchasedCredits > 0 && (
                  <p className="text-xs text-gray-500">
                    Includes {creditData.adCreative.purchasedCredits} purchased credits
                  </p>
                )}
              </div>
            )}

            {/* Team Seats */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">Team Seats</span>
                </div>
                <span className="text-sm text-gray-600">
                  {usageSummary.currentTeamSeatsUsed} / {formatLimit(usageSummary.teamSeatsLimit)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(teamSeatsPercentage)}`}
                  style={{ width: `${teamSeatsPercentage}%` }}
                ></div>
              </div>
            </div>

            {/* Projects */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">Projects</span>
                </div>
                <span className="text-sm text-gray-600">
                  {usageSummary.currentProjectsCount} / {formatLimit(usageSummary.projectsLimit)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(projectsPercentage)}`}
                  style={{ width: `${projectsPercentage}%` }}
                ></div>
              </div>
            </div>

            {/* Versions per Creative */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-700">Versions per Creative</span>
                </div>
                <span className="text-sm text-gray-600">
                  Up to {formatLimit(usageSummary.versionsLimit)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all duration-300 bg-orange-500"
                  style={{ width: '100%' }}
                ></div>
              </div>
              <p className="text-xs text-gray-500">
                {usageSummary.versionsLimit === -1 
                  ? 'Each creative can have unlimited versions' 
                  : `Each creative can have up to ${usageSummary.versionsLimit} versions`}
              </p>
            </div>
          </>
        )}

        {/* Storage Usage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-gray-700">Storage</span>
            </div>
            <span className="text-sm text-gray-600">
              {StorageLimitService.formatBytes(usageSummary.currentStorageUsed)} / {StorageLimitService.formatBytes(usageSummary.maxStorageBytes)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(usageSummary.storagePercentageUsed)}`}
              style={{ width: `${Math.min(usageSummary.storagePercentageUsed, 100)}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500">
            {StorageLimitService.formatBytes(usageSummary.storageRemaining)} remaining
          </p>
        </div>
      </div>

      {/* Upgrade Prompt */}
      {(usageSummary.isOverLimit || usageSummary.upgradeRecommendations.length > 0) && (
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <ArrowUp className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {usageSummary.isOverLimit ? 'Upgrade Required' : 'Consider Upgrading'}
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {usageSummary.upgradeRecommendations.map((recommendation, index) => (
                  <li key={index}>• {recommendation}</li>
                ))}
              </ul>
              {onUpgradeClick && (
                <button
                  onClick={onUpgradeClick}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Upgrade Plan
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Purchased Credits Info */}
      {(creditData?.adCreative?.purchasedCredits ?? 0) > 0 && (
        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-sm font-medium text-orange-800">
                <Sparkles className="w-3 h-3 inline mr-1" />
                {creditData.adCreative.purchasedCredits} purchased creative credits available
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageMeterWidget; 